// Virgin VS Chad — network protocol
// Role codes stay 'ext'/'int' internally (short), display names Virgin/Chad.

export type Role = 'ext' | 'int';

// Mirrored from src/match/modes.ts so the wire schema is self-contained.
// Keep both definitions in lock-step (test will fail if they drift).
export type MatchModeWire = 'classic' | 'tdm' | 'pve' | 'race';

// Mirrored from src/match/modes.ts ArenaId. Each match plays in one
// of these arenas; the map-vote system picks which on the ENDED phase.
export type ArenaIdWire =
  | 'classic_a' | 'classic_b'
  | 'tdm_a'     | 'tdm_b'
  | 'pve_a'     | 'pve_b'
  | 'race_a'    | 'race_b'
  | 'tiny_arena' | 'tiny_cross' | 'tiny_circle' | 'tiny_duel';

// W1 (perf refactor): the per-tick wire snapshot carries only the
// tick-variable "hot" fields below. Stable metadata lives in a separate
// `PlayerMeta` message the server emits on join / role-switch /
// cosmetic / legendary change. Clients merge meta cache into hot
// entries at the NetClient layer so every downstream consumer still
// sees a full `PlayerSnap`.
export type PlayerHotFields = {
  id: string;
  x: number;
  y: number;
  z: number;
  yaw: number;
  hp: number;
  kills: number;
  deaths: number;          // cumulative deaths this match (any cause)
  level: number;
  xp: number;
  hood: boolean;           // invisibility-active state
  alive: boolean;
  respawnInSec: number;    // 0 while alive
  powerups: string[];      // stacked powerup ids picked this match
  comboTier: number;       // 0 = neutral, up to COMBO.TIERS.length - 1
  comboRemainingSec: number; // 0 if no combo, else seconds until it falls off
  questMask: number;       // bitfield of your faction's venues visited (bit per venue)
  // Ultimate state — both factions share this shape; activation behavior
  // is per-role (Chad = Leap, Virgin = Ultra Instinct).
  ultCharge: number;        // 0..1 — fills over time + on damage dealt
  ultActive: boolean;       // true while leap arc / UI bubble is live
  // Mythical item system — single slot. `mythic` = equipped mythical id
  // (consumed on use, cleared to null). `viralUntilSec` > 0 = "Going
  // Viral" buff timer remaining (rainbow trail, +dmg, +speed). Server
  // writes both; clients render + gate the USE key off `mythic`.
  mythic: MythicalId | null;
  viralUntilSec: number;
  // Lobby state — true while the player is in the global lobby room
  // (vibing, no PvP damage). Clients use this to skip rendering
  // cross-state remotes so lobby and match viewers don't see each
  // other's sprites.
  lobby: boolean;
  // Admin-visible flag. True while a debug-enabled session has flipped
  // god mode on via setGodMode / toggleGodMode. Broadcast to every
  // client so enemies see a "GOD MODE" badge over invulnerable players
  // (can't-tell-why-no-damage is worse than a visible admin tag).
  godMode: boolean;
};

// Stable identity/cosmetic metadata. Sent via `playerMeta` on join,
// role swap, cosmetic equip change, and legendary pickup/drop. Clients
// maintain a `Map<id, PlayerMeta>` cache keyed by player id.
export type PlayerMeta = {
  id: string;
  name: string;
  role: Role;
  hpMax: number;
  xpNeeded: number;
  isBot: boolean;          // true for AI-controlled team-fill slots
  equipped: EquippedWire;  // remote cosmetic sync (hat/skin/pet/trail ids or null)
  legendary: string | null; // equipped legendary weapon id (from enemy boss drop)
  // Declared by the client on hello. True when the player is running a
  // debug-enabled session (Vite dev build OR `?debug=1`/`?dev=1` URL).
  // Every other client renders a "GM" badge over this player's sprite
  // so debug sessions are visible at a glance.
  debugMode: boolean;
};

// Downstream consumers (main.ts, HUD) keep seeing the full shape.
// Hot fields + meta fields. The `id` overlap collapses via `Omit`.
export type PlayerSnap = PlayerHotFields & Omit<PlayerMeta, 'id'>;

// Mythical items — ultra-rare chest drops. Single slot per player,
// consumed on use. See src/match/mythicals.ts for tuning.
export type MythicalId = 'deathnote' | 'going_viral';

export type EquippedWire = {
  hat: string | null;
  skin: string | null;
  pet: string | null;
  trail: string | null;
};

export type MatchPhase = 'warmup' | 'playing' | 'suddenDeath' | 'ended';

export type MatchState = {
  phase: MatchPhase;
  remainingSec: number;    // seconds left in the current phase
  matchId: number;
  mode: MatchModeWire;     // active mode for this match
  arenaId: ArenaIdWire;    // active arena variant (CP-A: vote winner)
  teamSize: number;        // 2/3/4 — auto-scales with human count
  kills: { ext: number; int: number };
  dayTime: number;
  dayNumber: number;
};

// Legacy aliases so carry-over code still compiles during the checkpoint rollover.
export type RoundPhase = MatchPhase;
export type RoundState = MatchState;

export type MatchMVP = {
  id: string;
  name: string;
  role: Role;
  kills: number;
};

// Ranked entry in the end-of-match podium. Sorted by kills desc, ties
// broken by lower id. Capped at top 3 on the wire.
export type Standing = {
  id: string;
  name: string;
  role: Role;
  kills: number;
};

export type MatchResult = {
  winner: 'ext' | 'int' | 'tie';
  kills: { ext: number; int: number };
  mvp: MatchMVP | null;
  standings: Standing[]; // length 0..3
  mode: MatchModeWire;     // mode that just finished
  nextMode: MatchModeWire; // mode that will start next (for the "Next: TDM" label)
};

export type FxKind =
  | 'hit' | 'kill' | 'hoodOn' | 'hoodOff' | 'respawn' | 'heal'
  | 'chestOpen' | 'chestGood' | 'chestBad'
  | 'bossHit' | 'bossHeal' | 'legendaryPickup'
  | 'ultLeapStart' | 'ultLeapLand' | 'ultUiStart' | 'ultUiEnd'
  // Mythical FX — 'mythicPickup' on grab, 'mythicDeathnote' on use
  // (sourceId = holder, targetId = victim), 'mythicViralStart' + End.
  | 'mythicPickup' | 'mythicDeathnote' | 'mythicViralStart' | 'mythicViralEnd'
  // Attack-orb visual — fired every tick for each attacker→target pair
  // in aura range. Cosmetic only; damage still applied via hit/state diff.
  // targetKind distinguishes player vs npc so the client resolves the
  // right map for position tracking.
  | 'aura';

export type FxTargetKind = 'player' | 'npc';

export type BossKindWire = 'redditMod' | 'gymfluencer';

// Neutral NPC snapshot — server-authoritative. See src/match/npc_aggro.ts
// for the mood-meter model: NPCs have no HP, they have a signed mood
// pulled by nearby players' absorption, and at saturation they LEAVE the
// map instead of dying.
export type NpcStage = 0 | 1 | 2 | 3;
export type NpcSnap = {
  id: number;
  x: number;
  z: number;
  yaw: number;
  stage: NpcStage;
  mood: number;              // -MOOD_RANGE..+MOOD_RANGE, sign = attacker faction
  shiny: boolean;            // triple-XP state after body-checking a player
  visible: boolean;          // false while respawning
};

// Rejoin payload — sent with `hello` when the client is re-entering a
// room it was already in (refresh / dropped connection). Applied by
// the server only when there is no reserved bot for this deviceId —
// covers the "Durable Object hibernated because the last human left"
// edge case where the bot-reservation path can't restore state. In a
// jam/testing context we trust the client; a production build would
// sign these or cap maxes. Fields are intentionally the same shape as
// the SelfSnapshotStash payload client-side.
export type HelloRejoin = {
  room: string;
  hp: number;
  hpMax: number;
  level: number;
  xp: number;
  xpNeeded: number;
  ultCharge: number;
  ultActive: boolean;
  kills: number;
  comboTier: number;
  powerups: string[];
  legendary: string | null;
  mythic: string | null;
  viralUntilSec: number;
  role: Role;
  x: number;
  z: number;
};

export type C2S =
  // `wireBinary: true` asks the server to emit `snapshot` frames as
  // binary ArrayBuffers per src/net/wire.ts rather than JSON text.
  // `wireDelta: true` (requires wireBinary) additionally layers
  // byte-run delta encoding against the last keyframe. Flags are
  // opt-in via ?binary=1 / ?delta=1 on the client URL.
  | { t: 'hello'; name: string; deviceId: string; prefer?: Role; wireBinary?: boolean; wireDelta?: boolean; rejoin?: HelloRejoin; debugMode?: boolean }
  | { t: 'input'; x: number; y: number; z: number; yaw: number; hood: boolean; tick: number; lobby?: boolean }
  | { t: 'hood'; on: boolean }
  | { t: 'pickPowerup'; id: string }
  | { t: 'healBoss' }
  | { t: 'pingCmd'; cmd: 'follow' | 'help' | 'wait' | 'danger' }
  | { t: 'setCosmetics'; equipped: EquippedWire }
  | { t: 'mvpClipUpload'; mime: string; b64: string }
  // Two-axis vote: client sends one field per click. The server
  // updates whichever axis arrived; the other tally is unaffected.
  | { t: 'mapVote'; mode?: MatchModeWire; arenaId?: ArenaIdWire }
  | { t: 'activateUlt' }
  | { t: 'leapExecute'; tx: number; tz: number }
  // Mythic use — targetId required for deathnote (victim player id),
  // ignored for going_viral (buff applies to caller).
  | { t: 'useMythic'; targetId?: string }
  // Dev-only mutations. Gated server-side via the PARTYKIT_DEV env var
  // (set only by `partykit dev` locally, not by `partykit deploy`).
  // Enables tests + the F8 debug console to drive state in MP.
  // `key` authorizes debug against a deployed worker that has
  // PARTYKIT_DEBUG_KEY set. Local `partykit dev` ignores the field
  // (PARTYKIT_DEV=1 is its own gate). Omitted when no key stored.
  | { t: 'debug'; op: DebugOp; args?: DebugArgs; key?: string }
  | { t: 'ping'; ts: number };

export type DebugOp =
  | 'setHp' | 'setMaxHp' | 'setLevel' | 'grantXp'
  | 'givePowerup' | 'giveLegendary'
  | 'teleport' | 'setGodMode' | 'killSelf'
  | 'chargeUlt'
  | 'setDamageMult' | 'setSpeedMult'
  | 'setRegenRate' | 'setCircleMult' | 'setRingMult' | 'setHitboxShrink'
  | 'spawnChest' | 'spawnBoss' | 'killBoss' | 'setBossHp'
  | 'spawnNpc' | 'setNpcCount'
  | 'setMode' | 'endMatch' | 'switchRole'
  | 'spawnBot' | 'despawnBot';

export type DebugArgs = Record<string, unknown>;

export type ChestSnapWire = { id: number; x: number; z: number };
// `kind: 'mythic'` = purple orb carrying a mythical. The `mythic` field
// is set for that kind so the client can render the specific icon
// (deathnote book vs viral camera flash). xp/hp items omit it.
export type LootSnapWire = {
  id: number;
  x: number;
  z: number;
  role: Role;
  kind: 'xp' | 'hp' | 'mythic';
  mythic?: MythicalId;
};

export type BossSnapWire = {
  id: number;
  kind: BossKindWire;
  side: Role;
  x: number;
  z: number;
  hp: number;
  hpMax: number;
  targetId: string;
};

export type LegendarySnapWire = {
  id: number;
  x: number;
  z: number;
  weapon: string;
  team: Role;
};

export type S2C =
  | { t: 'welcome'; id: string; seed: number; role: Role; tickRate: number; x: number; z: number; mode: MatchModeWire; arenaId: ArenaIdWire }
  | {
      t: 'snapshot';
      tick: number;
      // On the wire this carries PlayerHotFields[]. Downstream consumers
      // subscribe via NetClient.on() which merges the meta cache in so
      // they continue to observe full `PlayerSnap[]`. If you're reading
      // msg.players raw (e.g. an interceptor), cast to PlayerHotFields[].
      players: PlayerSnap[];
      match: MatchState;
      npcs: NpcSnap[];
      chests: ChestSnapWire[];
      loot: LootSnapWire[];
      bosses: BossSnapWire[];
      legendaries: LegendarySnapWire[];
    }
  // Stable player metadata; emitted on connect, role swap, cosmetic
  // equip change, and legendary pickup/drop. Clients cache by id.
  // `full: true` means "this is the complete roster, prune anyone not
  // in it" (sent once on join). Partial broadcasts leave unlisted
  // players' meta intact.
  | { t: 'playerMeta'; players: PlayerMeta[]; full?: boolean }
  | { t: 'pong'; ts: number }
  | { t: 'toast'; msg: string }
  | { t: 'fx'; kind: FxKind; x: number; z: number; targetId?: string; sourceId?: string; targetKind?: FxTargetKind }
  | { t: 'npcShout'; npcId: number; x: number; z: number; mood: number }
  | { t: 'matchResult'; result: MatchResult }
  | { t: 'killFeed'; killer: string; victim: string; role: Role }
  | { t: 'bossSpawn'; kind: BossKindWire; side: Role; x: number; z: number }
  | { t: 'bossDeath'; kind: BossKindWire; lootTeam: Role; x: number; z: number }
  | { t: 'mvpClipBroadcast'; mvpId: string; mime: string; b64: string }
  // Two-axis map vote — fired on playing→ended with both candidate
  // sets (modes + arenas) and a wall-clock deadline. Clients render
  // the modal until endsAtMs, then auto-close.
  | { t: 'mapVoteOpen'; modes: MatchModeWire[]; arenas: ArenaIdWire[]; endsAtMs: number }
  // Live tally updates — both tallies broadcast on any vote so clients
  // stay in sync with either axis changing.
  | { t: 'mapVoteUpdate'; modeTally: Record<string, number>; arenaTally: Record<string, number> };

// Shared constants — used by both server and client.
export const QUIET_ZONES: ReadonlyArray<{ x: number; z: number; r: number }> = [
  { x: 0, z: 0, r: 4.5 },
  { x: -44, z: 44, r: 4.5 },
  { x: 44, z: -44, r: 4.5 },
];

export function inAnyQuietZone(x: number, z: number): boolean {
  for (const q of QUIET_ZONES) {
    const dx = x - q.x;
    const dz = z - q.z;
    if (dx * dx + dz * dz <= q.r * q.r) return true;
  }
  return false;
}

// Faction bases — server uses these for respawn; client uses them for
// spawn points. Values match the Classic 80u arena default. CP4+
// switches callers to read from `arenaForMode(mode).factionBases`
// directly so each mode can have its own bases.
export const FACTION_BASES: Record<Role, { x: number; z: number }> = {
  int: { x: -35, z: 0 },   // Virgin base (left, Classic 80u)
  ext: { x:  35, z: 0 },   // Chad base (right, Classic 80u)
};

export function homeForDeviceId(deviceId: string): { x: number; z: number } {
  if (!deviceId) return { x: 0, z: 0 };
  let h = 0;
  for (let i = 0; i < deviceId.length; i++) h = ((h * 31) + deviceId.charCodeAt(i)) >>> 0;
  // Retained for backward-compat; in match mode everyone respawns at their FACTION_BASE.
  return { x: (h % 60) - 30, z: ((h >> 4) % 40) - 20 };
}

export const DAY_LENGTH_SEC = 1200;

// Combat tuning (used by server authoritative tick)
export const COMBAT = {
  WARMUP_SEC: 10,
  MATCH_SEC: 300,
  SUDDEN_DEATH_SEC: 60,
  ENDED_SEC: 10,
  RESPAWN_SEC: 5,
  HP_MAX: 100,
  DAMAGE_PER_SEC: 15,
  // Passive health regen — kicks in after REGEN_DELAY_SEC of no damage.
  // Full heal from empty in ~10s so it's noticeable between fights but
  // doesn't erase a committed engagement.
  REGEN_DELAY_SEC: 5,
  REGEN_PER_SEC: 10,
  // Faction weapon shapes. Damage PER-SECOND is identical (see
  // COMBAT.DAMAGE_PER_SEC below + symmetric formula in party/town.ts
  // combatTick). The shapes are the design distinction:
  //   Chad: solid circle — close-range disc, always hits at 0..R.
  //   Virgin: ring with dead zone — must position at the outer band.
  // Sizes tuned so ring area > circle area to compensate for the
  // harder positioning constraint.
  CHAD_CIRCLE_R: 3.0,
  VIRGIN_RING_INNER: 4.0,
  VIRGIN_RING_OUTER: 5.0,
  MIN_XP_PER_LEVEL: 100,
  XP_LEVEL_SCALING: 1.35,
  // ---- Ultimates ----
  // Charge fills passively + bonus per kill. 0..1; activation requires 1.
  ULT_CHARGE_PER_SEC: 1 / 45,    // 45s passive fill from empty
  ULT_CHARGE_PER_KILL: 0.25,
  // Chad Leap — Diablo-barbarian-style hold-aim jump.
  // ULT_LEAP_DIST is the legacy fixed forward distance (no longer used
  // by the active aim flow but kept for any callsite that still reads
  // it). Active flow uses MAX_R as the aim cap.
  ULT_LEAP_DIST: 9,
  ULT_LEAP_DUR_MS: 700,           // base duration; total = base + dist*50ms
  ULT_LEAP_AOE_R: 4.0,
  ULT_LEAP_DAMAGE: 150,           // 2.3× the old 65 — pays for the aim time
  ULT_LEAP_MAX_R: 12,             // outer reticle ring radius
  ULT_LEAP_MIN_R: 1,              // minimum committable distance
  ULT_LEAP_AIM_SPEED: 14,         // u/s WASD steering rate
  // Virgin Ultra Instinct — slow time for non-int entities.
  ULT_UI_DUR_MS: 5000,
  ULT_UI_TIME_SCALE: 0.45,       // movement + damage rate of slowed entities
  // ---- Mythicals ----
  // Deathnote: instakill one enemy. Lockout at match start so nobody
  // gets one-shot from spawn. Range cap keeps it from being a
  // cross-map snipe; within-ring is plenty for a 160u arena.
  MYTHIC_DEATHNOTE_LOCKOUT_SEC: 30,
  MYTHIC_DEATHNOTE_MAX_RANGE: 35,
  // Going Viral: timed Mario-star. Duration tuned so one viral ≈ one
  // deathnote's kill potential over the window (2-3 frags).
  MYTHIC_VIRAL_DUR_MS: 6000,
  MYTHIC_VIRAL_DAMAGE_MULT: 1.6,
  MYTHIC_VIRAL_SPEED_MULT: 2.0,
};
